import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';

/**
 * WorkflowTemplateService - CRUD for admin-defined workflow templates.
 *
 * Templates are stored at schools/{schoolId}/workflowTemplates/{templateId}.
 * Each template contains an embedded steps array (single-doc pattern, like hierarchies).
 * Only admins can create/update/delete. All staff can read.
 */
class WorkflowTemplateService {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
    this.ttl = 2 * 60 * 1000; // 2 minutes
  }

  _cacheKey(schoolId) {
    return schoolId;
  }

  _collectionRef(schoolId) {
    return collection(db, 'schools', schoolId, 'workflowTemplates');
  }

  /**
   * Get all workflow templates for a school.
   */
  async getTemplates(schoolId, forceRefresh = false) {
    if (!schoolId) throw new Error('schoolId is required');

    const key = this._cacheKey(schoolId);
    const now = Date.now();

    if (!forceRefresh && this.cache.has(key) && (now - this.timestamps.get(key)) < this.ttl) {
      return this.cache.get(key);
    }

    try {
      const snap = await getDocs(this._collectionRef(schoolId));
      const templates = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });

      this.cache.set(key, templates);
      this.timestamps.set(key, now);
      return templates;
    } catch (error) {
      console.error('[WorkflowTemplateService] Error fetching templates:', error);
      throw error;
    }
  }

  /**
   * Get a single template by ID.
   */
  async getTemplate(schoolId, templateId) {
    if (!schoolId || !templateId) throw new Error('schoolId and templateId are required');

    try {
      const docRef = doc(db, 'schools', schoolId, 'workflowTemplates', templateId);
      const snap = await getDoc(docRef);
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() };
    } catch (error) {
      console.error('[WorkflowTemplateService] Error fetching template:', error);
      throw error;
    }
  }

  /**
   * Create a new workflow template.
   */
  async createTemplate(schoolId, templateData, userId) {
    if (!schoolId) throw new Error('schoolId is required');
    if (!templateData.name?.trim()) throw new Error('Template name is required');

    const template = {
      name: templateData.name.trim(),
      description: templateData.description?.trim() || '',
      category: templateData.category || 'general',
      icon: templateData.icon || 'clipboard-list',
      active: templateData.active !== false,
      trigger: {
        type: templateData.trigger?.type || 'manual',
        eventSource: templateData.trigger?.eventSource || null,
        conditions: templateData.trigger?.conditions || {},
      },
      steps: (templateData.steps || []).map((step, i) => ({
        id: step.id || `step_${Date.now()}_${i}`,
        order: step.order ?? i,
        name: step.name || `Step ${i + 1}`,
        type: step.type || 'todo',
        config: {
          title: step.config?.title || '',
          description: step.config?.description || '',
          priority: step.config?.priority || 'normal',
          eventType: step.config?.eventType || null,
          duration: step.config?.duration || null,
          // Notification fields
          category: step.config?.category || null,
          recipientRole: step.config?.recipientRole || null,
          actionUrl: step.config?.actionUrl || null,
          actionLabel: step.config?.actionLabel || null,
          // Parent message fields
          messageType: step.config?.messageType || null,
          messagePriority: step.config?.messagePriority || null,
          // Recognition fields
          values: step.config?.values || null,
          points: step.config?.points || null,
          // Workflow action fields (for embedded task actions)
          actionType: step.config?.actionType || null,
          actionConfig: step.config?.actionConfig || {},
        },
        timing: {
          offsetDays: step.timing?.offsetDays ?? 0,
          offsetFrom: step.timing?.offsetFrom || 'trigger',
        },
        assignment: {
          type: step.assignment?.type || 'triggering_user',
          staffIds: step.assignment?.staffIds || [],
        },
        dependsOn: step.dependsOn || [],
      })),
      contextSchema: templateData.contextSchema || {},
      schoolId,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      const ref = await addDoc(this._collectionRef(schoolId), template);
      this._invalidateCache(schoolId);
      return { id: ref.id, ...template };
    } catch (error) {
      console.error('[WorkflowTemplateService] Error creating template:', error);
      throw error;
    }
  }

  /**
   * Update a workflow template.
   */
  async updateTemplate(schoolId, templateId, updates) {
    if (!schoolId || !templateId) throw new Error('schoolId and templateId are required');

    const data = { ...updates, updatedAt: serverTimestamp() };
    delete data.schoolId;
    delete data.createdBy;
    delete data.createdAt;

    try {
      const docRef = doc(db, 'schools', schoolId, 'workflowTemplates', templateId);
      await updateDoc(docRef, data);
      this._invalidateCache(schoolId);
      return { id: templateId, ...data };
    } catch (error) {
      console.error('[WorkflowTemplateService] Error updating template:', error);
      throw error;
    }
  }

  /**
   * Delete a workflow template.
   */
  async deleteTemplate(schoolId, templateId) {
    if (!schoolId || !templateId) throw new Error('schoolId and templateId are required');

    try {
      const docRef = doc(db, 'schools', schoolId, 'workflowTemplates', templateId);
      await deleteDoc(docRef);
      this._invalidateCache(schoolId);
    } catch (error) {
      console.error('[WorkflowTemplateService] Error deleting template:', error);
      throw error;
    }
  }

  /**
   * Toggle a template's active state.
   */
  async toggleActive(schoolId, templateId, active) {
    return this.updateTemplate(schoolId, templateId, { active });
  }

  _invalidateCache(schoolId) {
    this.cache.delete(schoolId);
    this.timestamps.delete(schoolId);
  }
}

export const workflowTemplateService = new WorkflowTemplateService();
export default workflowTemplateService;
